import fastpanel from '@paperlesspost/fastpanel-js';
import releaseStage from './releaseStage';
import type { TrackedEvent } from '../types';

import forOwn from 'lodash/forOwn';
import isObject from 'lodash/isObject';

export let fastpanelQueue: TrackedEvent[] = [];

function getFastlyTrackingHost() {
  const hostname = window.location.hostname.toLowerCase();
  return hostname.indexOf('www') === 0
    ? 'https://trk.ppassets.com'
    : 'https://earth-trk.ppassets.com';
}

export function configureFastpanel() {
  const promise = new Promise(resolve => {
    const options = {
      api_host: getFastlyTrackingHost(),
      loaded: fastpanel => {
        const superId =
          fastpanel.get_property('superId') || fastpanel.get_distinct_id();

        // Data team expects `super_id` and not camelcase
        fastpanel.register_once({ super_id: superId });
        isLoaded(true);
        resolve(fastpanel);
      },
    };

    if (releaseStage() === 'production') {
      fastpanel.init('681f969629fb1e240622f04a045d8a41', options);
    } else {
      fastpanel.init('ace0d4a9067309a491b2a13cd9974158', options);
    }
  });

  return promise;
}

/*
 * alias
 *
 * This function takes no parameters and aliases "accountId" to "distinct_id",
 * basically allowing us to track users across sign up flows.
 *
 * You alias only once, on new user signup.
 *
 * Please note: you must call alias *before* calling identify, or
 * else you will get a race condition.
 *
 * For more information:
 * https://mixpanel.com/help/questions/articles/how-should-i-handle-my-user-identity-with-the-mixpanel-javascript-library
 */
export function alias(accountId: string) {
  fastpanel.alias(accountId);
}

/*
 * identify
 *
 * @param {string} id The identifier to be used in lieu of distinct_id, this is usually accountId.
 */
export function identify(id: string): void {
  fastpanel.identify(id);
}

let loaded: boolean = false;

export function isLoaded(fastpanelLoaded?: boolean): boolean {
  if (typeof fastpanelLoaded !== 'undefined') {
    loaded = fastpanelLoaded;
  }

  return loaded;
}

/*
 * registerOnce
 *
 * This function registers params to fastpanel through an object hash.
 * Every subsequent fastpanel event fired will continue to have the hash
 * properties persisted.
 */
export function registerOnce(params: Object): void {
  fastpanel.register_once(params);
}

export default function trackFastpanel(trackedEvent: TrackedEvent): void {
  if (isLoaded()) {
    trackFastpanelEvent(trackedEvent);
  } else {
    fastpanelQueue.push(trackedEvent);
  }
}

// This is mostly exposed to test, use the default export to make
// sure fastpanel is loaded before you track anything.

export function trackFastpanelEvent(trackedEvent: TrackedEvent) {
  const { data, name, global } = trackedEvent;

  // looks a little weird, but in the future we are hoping our analytics
  // solution wont need flattened objects; we are more or less
  // accommodating for fastpanel here.
  data.global = global;

  const trackedProperties = {};

  try {
    forOwn(data, (value, key) => {
      addProperty(trackedProperties, key, value);
    });
  } catch (e) {
    console.error(`Unable to track Mixpanel event ${name}!`, e);
    return;
  }

  fastpanel.track(name, trackedProperties);

  try {
    // Log the fastpanel event if the flag is set
    const logFlag = localStorage.getItem('_LogMP');
    if (logFlag && logFlag === 'on') console.log(trackedEvent);
  } catch (e) {
    // ignore the error if localstorage is full or blocked
  }
}

function addProperty(destination, key, value) {
  if (isObject(value) || Array.isArray(value)) {
    forOwn(value, (nestedValue, nestedKey) => {
      addProperty(destination, `${key}_${nestedKey}`, nestedValue);
    });
  } else {
    destination[key] = value;
  }
}
